import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { IPlot } from '../models/plot';

import { AnnotationsService } from './annotations.service';
import { createUID } from './util';

interface IUpdateXPos {
    position: number;
    id: string;
}

interface IZoom {
    start: number;
    end: number;
    id: string;
}

export type Mime = 'application/pdf' | 'image/jpeg' | 'image/png' | 'image/svg+xml';

export interface IExportConfig {
    plotId: string;
    filename: string;
    mime: Mime;
}

/**
 * A service to update multiple plots at once with observables
 * Contains globally updated values that can be shared between plots
 */
@Injectable({ providedIn: 'root' })
export class PlotsService {
    plots$: BehaviorSubject<IPlot[]>;
    removedPlots$: Subject<IPlot> = new Subject();
    plots: IPlot[] = [];
    xPosition$: BehaviorSubject<IUpdateXPos>;
    zoom$: BehaviorSubject<IZoom>;
    plotReflow$: Subject<null>;
    syncCrosshairs: boolean;
    syncZooms: boolean;
    syncYAxisLabelWidths: boolean;
    yAxisLabelWidths: Map<string, number>;
    yAxisLabelWidth$: BehaviorSubject<number>;
    downloadChartImage$: Subject<IExportConfig> = new Subject();

    constructor( private annotationsService: AnnotationsService ) {
        this.xPosition$ = new BehaviorSubject( null );
        this.zoom$ = new BehaviorSubject( null );
        this.plots$ = new BehaviorSubject( [] );
        this.plotReflow$ = new Subject();
        this.yAxisLabelWidth$ = new BehaviorSubject( null );
        this.yAxisLabelWidths = new Map();
    }

    /** returns a bahavior subject whose value is the current x axis value of the hovered point */
    getXPosition$(): BehaviorSubject<IUpdateXPos> {
        return this.xPosition$;
    }

    /** If sync crosshairs is enabled, push updated mouse positions to all plots */
    setXPosition( position: number, id: string ) {
        if ( this.syncCrosshairs ) {
            this.xPosition$.next({ position: position, id: id });
        }
    }

    /** set syncing of crosshairs between plots */
    enableCrosshairSync( enabled: boolean = true ) {
        this.syncCrosshairs = enabled;
        this.xPosition$.next( null );
    }

    /** set the global zoom level */
    setZoom( start: number, end: number, id: string ) {
        if ( this.syncZooms ) {
            this.zoom$.next({ start: start, end: end, id: id });
        }
    }

    /** returns an observable that emits when the global zoom range is updated */
    getZoom$() {
        return this.zoom$.pipe( debounceTime( 50 ) ); // prevent calling this too rapidly;
    }

    /** set zoom sync across plots */
    enableZoomSync( enabled: boolean = true ) {
        this.syncZooms = enabled;
        if ( !this.syncZooms ) {
            this.zoom$.next( null );
        }
    }

    /** returns an observable that emits when the parent wants the plots to resize themselves */
    getPlotReflow$() {
        return this.plotReflow$.pipe( debounceTime( 250 ));
    }

    /** alert the plots that they need to react to new sizing */
    reflowPlots() {
        this.plotReflow$.next();
    }

    /** add a plot to the plot list and assigns it a unique ID */
    addPlot( plot: IPlot ) {
        const newPlot = cloneDeep( plot ); // make sure we aren't referencing the passed object
        newPlot.plotId = plot.plotId != null ? plot.plotId : createUID();
        newPlot.state = 'expanded';
        this.plots.push( newPlot );
        this.plots$.next( this.plots );
    }

    /** return a plot from the list of plots, return -1 if plot id isn't found in the list */
    getPlotIndex( plotId: string ) {
        return this.plots.findIndex( plot => plot.plotId === plotId );
    }

    /** returns an observable that emits whenever we update the plot list */
    getPlots$() {
        return this.plots$;
    }

    getAllplotIds() {
        return this.plots$.getValue().map( plot => plot.plotId );
    }

    /** remove a plot from the plot list based on plot id */
    removePlot( id: string ) {
        const index = this.getPlotIndex( id );
        if ( index >= 0 ) {
            const removedPlot = this.plots.splice( index, 1 )[0];
            this.removedPlots$.next( removedPlot );
            this.plots$.next( this.plots );

            // when a plot is removed, recalculate global properties
            this.annotationsService.removeAnnotations( id );
            this.yAxisLabelWidths.delete( id );
            this.getMaxWidth();
        }
    }

    /** method to update a plot property */
    updatePlot( id: string, property: string, update: any ) {
        const index = this.getPlotIndex( id );
        if ( index >= 0 ) {
            const plot = this.plots[ index ];
            plot[ property ] = update;
            this.plots$.next( this.plots );
        } else {
            console.warn('unable to update plot: ID not found');
        }
    }

    /** set the plot list. Create a uniqueID for each plot in the new list */
    setPlots( plots: IPlot[] ) {
        this.plots = plots.map( plot => {
            plot.plotId = plot.plotId != null ? plot.plotId : createUID();
            return plot;
        });
        this.plots$.next( this.plots );
    }

    /**
     * Add the datasets from idB to the dataset from idA
     * If the dataset types don't match return -1 and don't combine
     * Otherwise return the number of datasets added to the plot
     */
    combinePlots( idA: string, idB: string ): number {
        if ( this.canCombine( idA, idB ) ) {
            const plotA = this.plots.find( plot => plot.plotId === idA );
            const plotB = this.plots.find( plot => plot.plotId === idB );
            // the spread operator ensures a new object is made
            // otherwise angular change detection will not fire
            plotA.datasets = [ ...plotA.datasets, ...plotB.datasets ];
            this.removePlot( idB );
            return plotB.datasets.length;
        }
        return -1;
    }

    /** Two plots can combine if they have datasets of similar type */
    canCombine( idA: string, idB: string ) {
        const indexA = this.getPlotIndex( idA );
        const indexB = this.getPlotIndex( idB );
        if ( indexA < 0 || indexB < 0 ) { return false; }
        const plotA = this.plots[ indexA ];
        const plotB = this.plots[ indexB ];

        if ( typeof plotA.type === 'undefined' || typeof plotB.type === 'undefined' ) {
            return true;
        } else {
            return plotA.type === plotB.type;
        }
    }

    /** create a new plot for every dataset in an existing plot */
    splitPlot( id: string ) {
        const index = this.getPlotIndex( id );
        if ( index >= 0 ) {
            const plot = this.plots[ index ];
            plot.datasets.forEach( dataset => {
                this.addPlot({
                    datasets: [ dataset ],
                    range: plot.range,
                    state: plot.state
                });
            });
            this.removePlot( id );
        }
    }

    /** enable synchronization of the y axis label widths */
    enableYAxisLabelSync( enabled = true ) {
        this.syncYAxisLabelWidths = enabled;
        if ( enabled ) {
            this.getMaxWidth();
        } else {
            this.yAxisLabelWidth$.next( null );
        }
    }

    /** set a new y axis label width only if greater than the current max label */
    setYAxisLabelWidth( plotId: string, newWidth: number ) {
        if ( plotId != null && newWidth != null ) {
            this.yAxisLabelWidths.set( plotId, newWidth );
            this.getMaxWidth();
        }
    }

    // from the array of axis widths, find the largest one
    getMaxWidth() {
        const maxWidth = Math.max( ...Array.from( this.yAxisLabelWidths.values() ));
        if ( this.syncYAxisLabelWidths && maxWidth > 0 ) {
            this.yAxisLabelWidth$.next( maxWidth );
        }
    }

    /** return the observable stream of max y label widths */
    getYAxisLabelWidth$() {
        return this.yAxisLabelWidth$.pipe( debounceTime( 200 ), distinctUntilChanged() );
    }

    /** tell our chart component that we want to download an image */
    setDownloadChartImage( plotId: string, filename: string, mimeType: Mime ) {
        this.downloadChartImage$.next({
            plotId: plotId,
            filename: filename,
            mime: mimeType
        });
    }

    /** returns an observable that emits a value when we want to download an image */
    getDownloadChartImage$() {
        return this.downloadChartImage$;
    }
}
